// ============================================================================
// CASE NUMBER FORMAT FORM MODAL — Add / Edit (CFG-CASENO-001)
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { caseNumberFormatService } from 'api/services/caseNumberFormatService';
import type { CaseNumberFormat, ResetCadence } from 'api/types/caseNumberFormat.types';
import { useCaseTypeOptions } from 'hooks/api/useCaseTypes';

type FmtStatus = 'ACTIVE' | 'INACTIVE';

interface FormatFormData {
  formatCode: string;
  formatName: string;
  caseTypeCode: string;
  prefix: string;
  infixPattern: string;
  sequencePadding: string;
  resetCadence: ResetCadence;
  lastSequenceValue: string;
  lastResetAt: string;
  description: string;
  effectiveFrom: string;
  effectiveTo: string;
  statusCode: FmtStatus;
}

const today = () => new Date().toISOString().split('T')[0];

const INITIAL_FORM_DATA: FormatFormData = {
  formatCode: '',
  formatName: '',
  caseTypeCode: '',
  prefix: '',
  infixPattern: '',
  sequencePadding: '6',
  resetCadence: 'NEVER',
  lastSequenceValue: '0',
  lastResetAt: '',
  description: '',
  effectiveFrom: today(),
  effectiveTo: '',
  statusCode: 'ACTIVE',
};

const RESET_OPTIONS = [
  { value: 'NEVER', label: 'Never' },
  { value: 'YEARLY', label: 'Yearly (Jan 1)' },
  { value: 'MONTHLY', label: 'Monthly (1st)' },
  { value: 'FY_INDIA', label: 'India Fiscal Year (Apr 1)' },
];

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const dateOnly = (iso?: string | null) => (iso ? iso.split('T')[0] : '');

export interface CaseNumberFormatFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy' | 'view';
  record?: CaseNumberFormat | null;
  onSaved: () => void;
  onClose: () => void;

  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const CaseNumberFormatFormModal: React.FC<CaseNumberFormatFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  const { options: caseTypeOptions } = useCaseTypeOptions();

  const [formData, setFormData] = useState<FormatFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData); formDataRef.current = formData;
  const recordRef = useRef(record); recordRef.current = record;
  const initialSnapshotRef = useRef('');
  const modeRef = useRef(mode); modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors(p => { if (!p[f]) return p; const n = { ...p }; delete n[f]; return n; });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; setFieldErrors({}); return; }
    const rec = recordRef.current;
    const data: FormatFormData = rec ? {
      formatCode: rec.formatCode,
      formatName: rec.formatName,
      caseTypeCode: rec.caseTypeCode,
      prefix: rec.prefix,
      infixPattern: rec.infixPattern ?? '',
      sequencePadding: String(rec.sequencePadding ?? 6),
      resetCadence: rec.resetCadence,
      lastSequenceValue: String(rec.lastSequenceValue ?? 0),
      lastResetAt: dateOnly(rec.lastResetAt),
      description: rec.description ?? '',
      effectiveFrom: dateOnly(rec.effectiveFrom),
      effectiveTo: dateOnly(rec.effectiveTo),
      statusCode: (rec.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as FmtStatus,
    } : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.formatCode || currentFormData.formatCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.formatCode?.trim();
      const name = currentFormData.formatName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.formatCode || currentRecord?.formatCode || 'Format'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;

    minimize({
      id: modalId, title, moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.formatCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.formatCode.trim();
    const name = formData.formatName.trim();
    const prefix = formData.prefix.trim();
    const padNum = Number(formData.sequencePadding);
    const seqNum = Number(formData.lastSequenceValue);

    if (!code) errors.formatCode = 'Format code is required';
    else if (!/^[A-Z0-9_-]+$/.test(code)) errors.formatCode = 'Uppercase letters, digits, hyphens, underscores only';

    if (!name) errors.formatName = 'Format name is required';

    if (!formData.caseTypeCode) errors.caseTypeCode = 'Case type is required';

    if (!prefix) errors.prefix = 'Prefix is required';
    else if (!/^[A-Z0-9_-]+$/.test(prefix)) errors.prefix = 'Uppercase letters, digits, hyphens, underscores only';

    if (!Number.isFinite(padNum) || padNum < 1 || padNum > 10)
      errors.sequencePadding = 'Padding must be between 1 and 10';

    if (!Number.isFinite(seqNum) || seqNum < 0)
      errors.lastSequenceValue = 'Last sequence value must be ≥ 0';

    if (!formData.effectiveFrom) errors.effectiveFrom = 'Effective From is required';
    if (formData.effectiveFrom && formData.effectiveTo
        && formData.effectiveFrom > formData.effectiveTo)
      errors.effectiveTo = 'Effective To must be on or after Effective From';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.querySelector<HTMLInputElement>('input,textarea')?.focus(); }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const code = formData.formatCode.trim().toUpperCase();
      const payload = {
        formatCode: code,
        formatName: formData.formatName.trim(),
        caseTypeCode: formData.caseTypeCode,
        prefix: formData.prefix.trim().toUpperCase(),
        infixPattern: formData.infixPattern.trim() || null,
        sequencePadding: Number(formData.sequencePadding),
        resetCadence: formData.resetCadence,
        lastSequenceValue: Number(formData.lastSequenceValue),
        lastResetAt: formData.lastResetAt || null,
        description: formData.description.trim() || null,
        effectiveFrom: formData.effectiveFrom,
        effectiveTo: formData.effectiveTo || null,
        statusCode: formData.statusCode,
      };

      if (mode === 'create' || mode === 'copy') {
        await caseNumberFormatService.create(payload);
        notificationService.toast.success(`Format "${code}" created successfully.`);
      } else if (record) {
        await caseNumberFormatService.update(record.id, { ...payload, rowVersion: record.rowVersion });
        notificationService.toast.success(`Format "${code}" updated successfully.`);
      }

      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        if (serverMsg?.toLowerCase().includes('modified')) {
          notificationService.toast.error('This format was modified by another user. Refresh and try again.', 6000);
        } else {
          notificationService.toast.error(serverMsg || 'A format with this code (or active for this case type) already exists.', 6000);
        }
      } else if (status === 400 && err?.response?.data?.errors?.length) {
        notificationService.toast.error((err.response.data.errors as string[]).join(' '), 6000);
      } else {
        notificationService.toast.error(serverMsg || 'Failed to save format. Please try again.', 6000);
      }
    }
  }, [formData, mode, record, onSaved, onClose]);

  const handleDiscard = useCallback(() => { setDiscardConfirmOpen(false); onClose(); }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem',
              border: '3px solid var(--border-color)', borderTopColor: '#2563eb',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'view' ? 'View Case Number Format' : mode === 'copy' ? 'Copy Case Number Format' : mode === 'create' ? 'Add Case Number Format' : 'Edit Case Number Format'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={mode === 'view'} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          <div className="form-grid-2">
            <div className="form-group" data-field="formatCode">
              <label className="form-label">Format Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('formatCode') ? ' form-field-error' : ''}`}
                value={formData.formatCode}
                maxLength={40}
                placeholder="ARB-DEFAULT"
                onChange={(e) => { clearFieldError('formatCode'); setFormData(p => ({ ...p, formatCode: e.target.value.toUpperCase() })); }}
                disabled={isSaving}
              />
              {getFieldError('formatCode') && <div className="form-error-message">{getFieldError('formatCode')}</div>}
            </div>

            <div className="form-group" data-field="formatName">
              <label className="form-label">Format Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('formatName') ? ' form-field-error' : ''}`}
                value={formData.formatName}
                maxLength={200}
                placeholder="Arbitration Default Numbering"
                onChange={(e) => { clearFieldError('formatName'); setFormData(p => ({ ...p, formatName: e.target.value })); }}
                disabled={isSaving}
              />
              {getFieldError('formatName') && <div className="form-error-message">{getFieldError('formatName')}</div>}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="caseTypeCode">
              <SyutSelect
                label={<>Case Type <span className="text-danger">*</span></>}
                required
                options={caseTypeOptions}
                value={formData.caseTypeCode}
                onChange={(v) => { clearFieldError('caseTypeCode'); setFormData(p => ({ ...p, caseTypeCode: (v as string) || '' })); }}
                error={getFieldError('caseTypeCode')}
                isClearable={false}
                isDisabled={mode === 'view' || (isSaving)}
              />
            </div>

            <div className="form-group" data-field="prefix">
              <label className="form-label">Prefix <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('prefix') ? ' form-field-error' : ''}`}
                value={formData.prefix}
                maxLength={20}
                placeholder="ARB"
                onChange={(e) => { clearFieldError('prefix'); setFormData(p => ({ ...p, prefix: e.target.value.toUpperCase() })); }}
                disabled={isSaving}
              />
              {getFieldError('prefix') && <div className="form-error-message">{getFieldError('prefix')}</div>}
            </div>
          </div>

          <div className="form-group" data-field="infixPattern">
            <label className="form-label">Infix Pattern</label>
            <input
              type="text"
              className="form-input"
              value={formData.infixPattern}
              maxLength={60}
              placeholder="ARB/{YYYY}/{NNNNNN}"
              onChange={(e) => setFormData(p => ({ ...p, infixPattern: e.target.value }))}
              disabled={isSaving}
            />
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              Tokens: <code>{'{YYYY}'}</code>, <code>{'{YY}'}</code>, <code>{'{MM}'}</code>, <code>{'{FY_INDIA}'}</code>, <code>{'{BRANCH}'}</code>, <code>{'{ENTITY}'}</code>, <code>{'{NNNNNN}'}</code> (sequence)
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="sequencePadding">
              <label className="form-label">Sequence Padding (1–10) <span className="text-danger">*</span></label>
              <input
                type="number"
                min={1}
                max={10}
                className={`form-input${getFieldError('sequencePadding') ? ' form-field-error' : ''}`}
                value={formData.sequencePadding}
                onChange={(e) => { clearFieldError('sequencePadding'); setFormData(p => ({ ...p, sequencePadding: e.target.value })); }}
                disabled={isSaving}
              />
              {getFieldError('sequencePadding') && <div className="form-error-message">{getFieldError('sequencePadding')}</div>}
            </div>

            <div className="form-group" data-field="resetCadence">
              <SyutSelect
                label={<>Reset Cadence <span className="text-danger">*</span></>}
                required
                options={RESET_OPTIONS}
                value={formData.resetCadence}
                onChange={(v) => setFormData(p => ({ ...p, resetCadence: ((v as string) || 'NEVER') as ResetCadence }))}
                isClearable={false}
                isDisabled={mode === 'view' || (isSaving)}
              />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="lastSequenceValue">
              <label className="form-label">Last Sequence Value</label>
              <input
                type="number"
                min={0}
                className={`form-input${getFieldError('lastSequenceValue') ? ' form-field-error' : ''}`}
                value={formData.lastSequenceValue}
                onChange={(e) => { clearFieldError('lastSequenceValue'); setFormData(p => ({ ...p, lastSequenceValue: e.target.value })); }}
                disabled={isSaving}
              />
              {getFieldError('lastSequenceValue') && <div className="form-error-message">{getFieldError('lastSequenceValue')}</div>}
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                Caution: changing this affects the next allocated case number. Use 0 for fresh sequences; set to a higher value to skip ranges or migrate from legacy systems.
              </div>
            </div>

            <div className="form-group" data-field="lastResetAt">
              <label className="form-label">Last Reset At</label>
              <input
                type="date"
                className="form-input"
                value={formData.lastResetAt}
                onChange={(e) => setFormData(p => ({ ...p, lastResetAt: e.target.value }))}
                disabled={isSaving}
              />
            </div>
          </div>

          <div className="form-group" data-field="description">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={formData.description}
              rows={2}
              onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
              disabled={isSaving}
            />
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="effectiveFrom">
              <label className="form-label">Effective From <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-input${getFieldError('effectiveFrom') ? ' form-field-error' : ''}`}
                value={formData.effectiveFrom}
                onChange={(e) => { clearFieldError('effectiveFrom'); setFormData(p => ({ ...p, effectiveFrom: e.target.value })); }}
                disabled={isSaving}
              />
              {getFieldError('effectiveFrom') && <div className="form-error-message">{getFieldError('effectiveFrom')}</div>}
            </div>

            <div className="form-group" data-field="effectiveTo">
              <label className="form-label">Effective To</label>
              <input
                type="date"
                className={`form-input${getFieldError('effectiveTo') ? ' form-field-error' : ''}`}
                value={formData.effectiveTo}
                onChange={(e) => { clearFieldError('effectiveTo'); setFormData(p => ({ ...p, effectiveTo: e.target.value })); }}
                disabled={isSaving}
              />
              {getFieldError('effectiveTo') && <div className="form-error-message">{getFieldError('effectiveTo')}</div>}
            </div>
          </div>

          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              required
              options={STATUS_OPTIONS}
              value={formData.statusCode}
              onChange={(v) => setFormData(p => ({ ...p, statusCode: ((v as string) || 'ACTIVE') as FmtStatus }))}
              isClearable={false}
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>
        </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{mode === 'view' ? 'Close' : 'Cancel'}</button>
          {mode !== 'view' && (<button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'copy' ? 'Save' : mode === 'create' ? 'Save' : 'Update'}
          </button>)}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'copy' ? 'Confirm Create' : mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={(mode === 'create' || mode === 'copy')
          ? `Create case number format "${formData.formatCode.trim().toUpperCase()}"?`
          : `Update case number format "${formData.formatCode.trim().toUpperCase()}"?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default CaseNumberFormatFormModal;
